import type { z } from 'zod';
import {
  Activity,
  Building,
  Building2,
  Earth,
  Globe,
  Hash,
  Mail,
  MapPin,
  Phone,
  Users,
} from 'lucide-react';

import type { ReportSchema } from '@ballerine/common';

import { ctw } from '@/common';
import { DataRow, EntityStatusIndicator } from '../../primitives';
import {
  filterValidStrings,
  resolveMerchantAddressDisplay,
  resolveMerchantEmailDisplay,
  resolveMerchantPhoneDisplay,
} from './utils';

type MerchantDetailsContentProps = {
  merchantDetails: NonNullable<z.infer<typeof ReportSchema>['data']>['merchantDetails'] | null;
  className?: string;
  showWebsite?: boolean;
  showAssociatedPersons?: boolean;
  emptyLabel?: string;
};

export const MerchantDetailsContent: React.FC<MerchantDetailsContentProps> = ({
  merchantDetails,
  className,
  showWebsite = false,
  showAssociatedPersons = false,
  emptyLabel = 'Not Detected',
}) => {
  if (!merchantDetails) {
    return null;
  }

  const phoneDisplay = resolveMerchantPhoneDisplay(merchantDetails);
  const emailDisplay = resolveMerchantEmailDisplay(merchantDetails);
  const addressDisplay = resolveMerchantAddressDisplay(merchantDetails);

  return (
    <div className={ctw(className)}>
      <DataRow
        label="Company Name"
        Icon={Building}
        value={merchantDetails.companyName}
        copyable={!!merchantDetails.companyName}
        copyValue={merchantDetails.companyName || ''}
        emptyLabel={emptyLabel}
        disableTranslation
      />

      <DataRow
        label="Alternative Company Names"
        Icon={Building2}
        value={(() => {
          const names = filterValidStrings(merchantDetails.alternativeCompanyNames);

          if (!names.length) return null;

          return <span>{names.join(', ')}</span>;
        })()}
        copyable={!!filterValidStrings(merchantDetails.alternativeCompanyNames).length}
        copyValue={filterValidStrings(merchantDetails.alternativeCompanyNames).join(', ') || ''}
        emptyLabel={emptyLabel}
        disableTranslation
      />

      <DataRow
        label="Entity Status"
        Icon={Activity}
        value={
          <EntityStatusIndicator
            status={(merchantDetails as Record<string, unknown>).status as string | null}
            emptyLabel={emptyLabel}
          />
        }
        copyable={!!(merchantDetails as Record<string, unknown>).status}
        copyValue={((merchantDetails as Record<string, unknown>).status as string) || ''}
        emptyLabel={emptyLabel}
      />

      <DataRow
        label="Registration Number"
        Icon={Hash}
        value={merchantDetails.registrationNumber}
        copyable={!!merchantDetails.registrationNumber}
        copyValue={merchantDetails.registrationNumber || ''}
        emptyLabel={emptyLabel}
      />

      <DataRow
        label="Phone Number"
        Icon={Phone}
        value={phoneDisplay.text}
        copyable={Boolean(phoneDisplay.text)}
        copyValue={phoneDisplay.copy}
        emptyLabel={emptyLabel}
      />

      <DataRow
        label="Email"
        Icon={Mail}
        value={emailDisplay.text}
        copyable={Boolean(emailDisplay.text)}
        copyValue={emailDisplay.copy}
        emptyLabel={emptyLabel}
        disableTranslation
      />

      <DataRow
        label="Country"
        Icon={Earth}
        value={merchantDetails.country}
        copyable={!!merchantDetails.country}
        copyValue={merchantDetails.country || ''}
        emptyLabel={emptyLabel}
      />

      <DataRow
        label="Address"
        Icon={MapPin}
        value={addressDisplay.text}
        copyable={Boolean(addressDisplay.text)}
        copyValue={addressDisplay.copy}
        emptyLabel={emptyLabel}
      />

      {showWebsite && (
        <DataRow
          label="Website"
          Icon={Globe}
          value={
            merchantDetails.website ? (
              <a
                href={merchantDetails.website}
                target="_blank"
                rel="noopener noreferrer"
                className="notranslate text-blue-600 hover:text-blue-800 hover:underline"
                translate="no"
              >
                {merchantDetails.website}
              </a>
            ) : null
          }
          copyable={!!merchantDetails.website}
          copyValue={merchantDetails.website || ''}
          emptyLabel={emptyLabel}
          disableTranslation
        />
      )}

      {showAssociatedPersons && (
        <DataRow
          label="Associated Persons"
          Icon={Users}
          value={(() => {
            const persons = filterValidStrings(merchantDetails.associatedPersons);

            if (!persons.length) return undefined;

            return (
              <div className="space-y-1">
                {persons.map((person, index) => (
                  <div key={index} className="notranslate text-sm text-gray-900" translate="no">
                    {person}
                  </div>
                ))}
              </div>
            );
          })()}
          copyable={!!filterValidStrings(merchantDetails.associatedPersons).length}
          copyValue={filterValidStrings(merchantDetails.associatedPersons).join(', ') || ''}
          emptyLabel={emptyLabel}
          disableTranslation
        />
      )}
    </div>
  );
};
